import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUSES = ["Pendente", "Em andamento", "Concluída", "Cancelada"];
const PRIORITIES = ["Crítica", "Alta", "Média", "Baixa"];

export default function ActionPlans() {
  const [searchParams] = useSearchParams();
  const initialCompany = searchParams.get("empresa") || "";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(initialCompany);
  const [statusFilter, setStatusFilter] = useState("");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      if (!companyId && r.data.length) setCompanyId(r.data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  const load = () => {
    if (!companyId) return;
    const params = { company_id: companyId };
    if (statusFilter) params.status = statusFilter;
    api.get("/absenteeism/action-plans", { params }).then((r) => setItems(r.data));
  };
  useEffect(load, [companyId, statusFilter]);  // eslint-disable-line

  const openNew = () => { setEditing(null); setShowForm(true); };
  const openEdit = (it) => { setEditing(it); setShowForm(true); };
  const onSaved = () => { setShowForm(false); load(); };
  const onDelete = async (it) => {
    if (!window.confirm("Excluir esta ação?")) return;
    try {
      await api.delete(`/absenteeism/action-plans/${it.id}`);
      toast.success("Ação removida.");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };
  const onChangeStatus = async (it, newStatus) => {
    try {
      await api.put(`/absenteeism/action-plans/${it.id}`, { ...it, status: newStatus });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  return (
    <div className="space-y-6" data-testid="abs-plans-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline">Absenteísmo</div>
          <h1 className="font-display text-3xl font-extrabold mt-1">Plano de ação</h1>
          <Link to="/app/absenteismo" className="text-sm text-emerald-800 hover:underline mt-2 inline-block">
            <i className="fa-solid fa-arrow-left mr-2"></i>Voltar ao painel
          </Link>
        </div>
        <div className="flex gap-2">
          <Link to={`/app/absenteismo/inteligencia?empresa=${companyId}`} className="btn btn-ghost border border-emerald-300 text-emerald-900">
            <i className="fa-solid fa-brain"></i> Inteligência IA
          </Link>
          <button onClick={openNew} className="btn btn-primary" data-testid="plan-new-btn">
            <i className="fa-solid fa-plus"></i> Nova ação
          </button>
        </div>
      </header>

      <div className="card flex flex-wrap items-end gap-3">
        <label className="flex-1 min-w-[220px]">
          <div className="overline mb-1">Empresa</div>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="w-full border border-slate-300 px-3 py-2">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.razao_social || c.nome_fantasia}</option>)}
          </select>
        </label>
        <label className="min-w-[180px]">
          <div className="overline mb-1">Status</div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full border border-slate-300 px-3 py-2">
            <option value="">Todos</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <div className="text-sm text-slate-500"><strong>{items.length}</strong> ação(ões)</div>
      </div>

      <div className="space-y-3" data-testid="plans-list">
        {items.length === 0 ? (
          <div className="card text-center py-12 text-slate-500">
            <i className="fa-solid fa-list-check text-4xl mb-3"></i>
            <div className="text-sm">Nenhuma ação cadastrada. Gere com a IA ou crie manualmente.</div>
          </div>
        ) : items.map((it) => (
          <div key={it.id} className="card" data-testid={`plan-row-${it.id}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
              <div className="font-display text-lg font-extrabold text-slate-900">{it.problema}</div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${
                  it.prioridade === "Crítica" ? "bg-rose-200 text-rose-900"
                  : it.prioridade === "Alta" ? "bg-amber-200 text-amber-900"
                  : "bg-slate-200 text-slate-700"}`}>{it.prioridade}</span>
                <select value={it.status} onChange={(e) => onChangeStatus(it, e.target.value)}
                        className="text-xs border border-slate-300 px-2 py-1"
                        data-testid={`plan-status-${it.id}`}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            {it.evidencia && <div className="text-xs text-slate-600 mb-1"><b>Evidência:</b> {it.evidencia}</div>}
            {it.relacao_nr01 && <div className="text-xs text-slate-600 mb-1"><b>Relação NR-01:</b> {it.relacao_nr01}</div>}
            <div className="text-sm mt-2"><b className="text-emerald-800">Ação:</b> {it.acao_recomendada}</div>
            <div className="flex items-center justify-between text-xs text-slate-500 mt-2">
              <div>
                {it.responsavel && <>Responsável: <b>{it.responsavel}</b></>}
                {it.prazo && <> · Prazo: <b>{it.prazo}</b></>}
                {it.source === "ai_analysis" && <> · <span className="text-emerald-700"><i className="fa-solid fa-wand-magic-sparkles"></i> gerado por IA</span></>}
              </div>
              <div className="flex gap-3">
                <button onClick={() => openEdit(it)} className="text-emerald-800 hover:underline">Editar</button>
                <button onClick={() => onDelete(it)} className="text-rose-700 hover:underline">Excluir</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && <PlanForm companyId={companyId} editing={editing} onClose={() => setShowForm(false)} onSaved={onSaved} />}
    </div>
  );
}

function PlanForm({ companyId, editing, onClose, onSaved }) {
  const [form, setForm] = useState(() => editing || {
    company_id: companyId,
    problema: "", evidencia: "", relacao_nr01: "",
    acao_recomendada: "", responsavel: "", prazo: "",
    prioridade: "Média", status: "Pendente", source: "manual",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.problema.trim() || !form.acao_recomendada.trim()) {
      toast.error("Problema e ação são obrigatórios."); return;
    }
    setBusy(true);
    try {
      const payload = { ...form, company_id: companyId };
      if (editing) await api.put(`/absenteeism/action-plans/${editing.id}`, payload);
      else         await api.post(`/absenteeism/action-plans`, payload);
      toast.success(editing ? "Ação atualizada." : "Ação criada.");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <h2 className="font-display text-xl font-extrabold">{editing ? "Editar ação" : "Nova ação"}</h2>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-3 text-sm">
          <label className="block md:col-span-2">
            <div className="overline mb-1">Problema identificado *</div>
            <input value={form.problema} onChange={set("problema")} className="w-full border border-slate-300 px-3 py-2" />
          </label>
          <label className="block md:col-span-2">
            <div className="overline mb-1">Evidência</div>
            <input value={form.evidencia} onChange={set("evidencia")} className="w-full border border-slate-300 px-3 py-2" placeholder="Ex: 5 CIDs F no setor Produção no último trimestre" />
          </label>
          <label className="block md:col-span-2">
            <div className="overline mb-1">Relação com NR-01 / Ergonomia</div>
            <input value={form.relacao_nr01} onChange={set("relacao_nr01")} className="w-full border border-slate-300 px-3 py-2" placeholder="Ex: Demandas de trabalho excessivas" />
          </label>
          <label className="block md:col-span-2">
            <div className="overline mb-1">Ação recomendada *</div>
            <textarea value={form.acao_recomendada} onChange={set("acao_recomendada")} rows={3} className="w-full border border-slate-300 px-3 py-2" />
          </label>
          <label className="block">
            <div className="overline mb-1">Responsável</div>
            <input value={form.responsavel} onChange={set("responsavel")} className="w-full border border-slate-300 px-3 py-2" />
          </label>
          <label className="block">
            <div className="overline mb-1">Prazo</div>
            <input value={form.prazo} onChange={set("prazo")} className="w-full border border-slate-300 px-3 py-2" placeholder="Ex: 30 dias" />
          </label>
          <label className="block">
            <div className="overline mb-1">Prioridade</div>
            <select value={form.prioridade} onChange={set("prioridade")} className="w-full border border-slate-300 px-3 py-2">
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="overline mb-1">Status</div>
            <select value={form.status} onChange={set("status")} className="w-full border border-slate-300 px-3 py-2">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>
        <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
          <button onClick={onClose} className="btn btn-ghost border border-slate-300">Cancelar</button>
          <button onClick={save} disabled={busy} className="btn btn-primary disabled:opacity-50">
            {busy ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}
